import * as React from 'react';

import { Icon } from 'elements';
import { ROUTES } from 'global/constants';
import { BannerList } from 'banners/components';
import { NotificationListContainer } from 'notifications/components';
import { NavLink } from 'react-router-dom';
import { SearchBox } from 'search/components/SearchBox';
import {
    Button,
    Dropdown,
    LabXChangeLogo,
    LoggedInMenu,
    ScreenReaderIntl,
    LocaleSwitcher,
} from 'ui/components';
import { WrappedMessage } from 'utils';
import messages from '../displayMessages';
import { intl } from 'i18n';

// transparent is a variant of dark where no background image will be displayed
export type HeaderBackgroundStyle = 'dark' | 'transparent' | 'light';

export interface HeaderProps {
    backgroundStyle: HeaderBackgroundStyle;
    // set to null to set even use the default background url
    backgroundUrl?: string|null;
    backgroundClassrooms?: boolean;
    backgroundExplore?: boolean;
    username: string;
    onLogIn: () => void;
    onLogOut: () => void;
    onSignup: () => void;
    onShowSidebar: () => void;
    notificationCount: number;
    messageCount?: number;
    searchUrl: string;
    children?: React.ReactNode;
    searchParams?: string[];
}

export interface HeaderState {
    scrollYPosition?: number;
    scrollYLastDirection: number;
    backgroundStyle: HeaderBackgroundStyle;
}

export class LoggedInView extends React.PureComponent<HeaderProps, {}> {
    public render() {
        const {onLogOut, username, notificationCount, searchUrl, searchParams} = this.props;
        const iconFill = this.props.backgroundStyle === 'light' ? '#003E6B' : 'white';
        return (
            <>
                <NavLink
                    className='header-nav-item hide-mobile'
                    activeClassName='active'
                    to={ROUTES.Explore.HOME}
                >
                    <WrappedMessage message={messages.uiNavExplore} />
                </NavLink>
                <NavLink
                    className='header-nav-item hide-mobile'
                    activeClassName='active'
                    to={ROUTES.Library.HOME}
                >
                    <WrappedMessage message={messages.uiNavLibrary} />
                </NavLink>
                <form className='search-bar form-inline mx-2'>
                    <SearchBox searchUrl={searchUrl} darkBackground={this.props.backgroundStyle !== 'light'} searchParams={searchParams} />
                </form>
                <NavLink
                    className='header-nav-item hide-mobile'
                    activeClassName='active'
                    to={ROUTES.Dashboard.HOME}
                >
                    <WrappedMessage message={messages.uiNavDashboard}/>
                </NavLink>
                <NavLink
                    className='header-nav-item hide-mobile'
                    activeClassName='active'
                    to={ROUTES.Community.HOME}
                >
                    <WrappedMessage message={messages.uiNavCommunity}/>
                </NavLink>
                <Dropdown
                    className='hide-mobile'
                    id='header-notify-dropdown'
                    label={<span className='icon-with-badge'>
                        <Icon name='bell' zoom='1.5em' outline fill={iconFill} />
                        {notificationCount > 0 &&
                            <span className='badge top'>
                                {notificationCount}
                                <ScreenReaderIntl
                                    message={messages.uiNotificationsSR}
                                    values={{count: notificationCount}}
                                />
                            </span>
                        }
                    </span>}
                    title={intl.formatMessage(messages.uiNotificationsButtonTitle)}
                >
                    <NotificationListContainer />
                </Dropdown>
                <NavLink
                    className='icon-nav-link hide-mobile'
                    activeClassName='active'
                    to={ROUTES.Messages.HOME}
                    title={intl.formatMessage(messages.uiMessagesLinkTitle)}>
                    <Icon name='message3' outline fill={iconFill} className='header-icon' zoom='1.5em' />
                </NavLink>
                <LoggedInMenu
                    className='hide-mobile'
                    onLogOut={onLogOut}
                    username={username}
                />
            </>
        );
    }
}

const LoggedOutView: React.SFC<HeaderProps> = (props: HeaderProps) => (
    <>
        <NavLink
            className='header-nav-item hide-mobile'
            activeClassName='active'
            to={ROUTES.Explore.HOME}
        >
            <WrappedMessage message={messages.uiNavExplore} />
        </NavLink>
        <NavLink
            className='header-nav-item hide-mobile'
            activeClassName='active'
            to={ROUTES.Library.HOME}
        >
            <WrappedMessage message={messages.uiNavLibrary} />
        </NavLink>
        <form className='search-bar form-inline mx-2'>
            <SearchBox searchUrl={props.searchUrl} darkBackground={props.backgroundStyle !== 'light'} searchParams={props.searchParams}/>
        </form>
        <div className='header-locale-switcher hide-mobile'>
            <LocaleSwitcher backgroundStyle={props.backgroundStyle} />
        </div>
        <div className='separator hide-tablet'/>
        <NavLink
            className='header-nav-item hide-tablet'
            activeClassName='active'
            to={ROUTES.General.ABOUT}
        >
            <WrappedMessage message={messages.uiAbout}/>
        </NavLink>
        <Button
            className='login-btn hide-mobile'
            btnStyle='outline'
            onClick={props.onLogIn}
            label={messages.uiSignIn}
        />
        <Button
            className='signup-btn hide-mobile'
            btnStyle='primary'
            onClick={props.onSignup}
            label={messages.uiSignUp}
        />
    </>
);

export class Header extends React.PureComponent<HeaderProps, HeaderState> {

    constructor(props: any) {
        super(props);
        this.state = {
            scrollYPosition: 0,
            scrollYLastDirection: 0,
            backgroundStyle: this.props.backgroundStyle,
        };
    }

    public updateColorOnScrollForClassName(className: string, originalColor: string, scrollPosition: number) {
        const elements = document.getElementsByClassName(className) as HTMLCollectionOf<HTMLElement>;
        /* can't use for-of for the for loop below because */
        /* HTMLCollectionOf<HTMLElement> does not support it */
        /* tslint:disable-next-line */
        for (let i = 0; i < elements.length; i++) {
            if (elements[i]) {
                if (scrollPosition === 0) {
                    elements[i].style.color = originalColor;
                } else {
                    elements[i].style.color = '#003E6B';
                }
            }
        }
    }

    public componentDidMount() {
        const headerNavItemClassName = 'header-nav-item';
        const headerNavItem = document.getElementsByClassName(headerNavItemClassName) as HTMLCollectionOf<HTMLElement>;
        const originalColorHeaderNavItem =  headerNavItem[0]?.style.color;
        const searchBoxInputClassName = 'search-box-input';
        const searchBoxInput = document.getElementsByClassName(searchBoxInputClassName) as HTMLCollectionOf<HTMLElement>;
        const originalColorSearchBoxInput =  searchBoxInput[0]?.style.color;

        document.addEventListener('scroll',
            (event) => {
                const target = event.target as HTMLDocument;
                const scrollYPosition = target?.scrollingElement?.scrollTop!;
                const scrollYLastDirection = scrollYPosition - this.state.scrollYPosition!;
                this.setState({scrollYPosition, scrollYLastDirection}, () => {
                    const backgroundStyle = (this.state.scrollYPosition === 0 || this.state.scrollYLastDirection > 0) ? this.props.backgroundStyle : 'light';
                    this.setState({backgroundStyle});
                });

                this.updateColorOnScrollForClassName(headerNavItemClassName, originalColorHeaderNavItem, scrollYPosition);
                this.updateColorOnScrollForClassName(searchBoxInputClassName, originalColorSearchBoxInput, scrollYPosition);
            }
        );
    }

    public render() {
        // The default hexagonal background does not need to be positioned or change blending.
        // Designs for classroom page header show some sort of fade, so background-blend-mode is used,
        // So that text is still readable with high contrast images set
        const extraStyles: any = {};
        if (this.props.backgroundClassrooms) {
            extraStyles.backgroundPosition = 'center';
            extraStyles.backgroundBlendMode = 'soft-light';
        }

        if (this.props.backgroundStyle === 'dark' && this.props.backgroundUrl !== null) {
            extraStyles.backgroundImage = `url(${this.props.backgroundUrl || '/assets/images/background-blue-desktop.svg'})`;
            extraStyles.backgroundSize = 'cover';
        }

        return (
            <header
                className={`global-header ${this.props.backgroundExplore ? 'dark' : this.state.backgroundStyle}`}
                style={extraStyles}
            >
                <BannerList sticky={this.state.scrollYPosition! > 0 ? (this.state.scrollYLastDirection < 0 ? true : true) : false}/>
                <div className='global-nav nav-placeholder'/>
                <nav className={`global-nav navbar ${this.state.scrollYPosition! > 0 ? (this.state.scrollYLastDirection < 0 ? 'scroll-up' : 'scroll-down') : ''}`}>
                    <button className='mobile-grabber btn btn-link' onClick={this.props.onShowSidebar}>
                    <span className='icon-with-badge'>
                        <Icon
                            name='grabber'
                            zoom='2em'
                            fill={this.state.backgroundStyle === 'light' ? '#003E6B' : 'white'}
                        />
                        {this.props.notificationCount > 0 &&
                        <span className='badge bottom'>
                                {this.props.notificationCount}
                            <ScreenReaderIntl
                                message={messages.uiNotificationsSR}
                                values={{count: this.props.notificationCount}}
                            />
                            </span>
                        }
                    </span>
                    </button>
                    <NavLink
                        className='logo-link'
                        to={ROUTES.All.HOME}
                    >
                        <LabXChangeLogo type='trim' theme={this.state.backgroundStyle === 'light' ? 'dark' : 'light'}/>
                    </NavLink>
                    {this.props.username
                        ? <LoggedInView {...this.props} backgroundStyle={this.state.backgroundStyle}/>
                        : <LoggedOutView {...this.props} backgroundStyle={this.state.backgroundStyle}/>
                    }
                </nav>
                {this.props.children}
            </header>
        );
    }
}
